import { useQueryClient } from '@tanstack/react-query';
import {
  Info,
  KeyRound,
  Loader2,
  Mail,
  Plus,
  RotateCw,
  Shield,
  Trash2,
  UserCog,
  Users,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  getAmbienteControllerListarCargosQueryKey,
  getAmbienteControllerListarConvitesQueryKey,
  getAmbienteControllerListarMembrosQueryKey,
  useAmbienteControllerCatalogoDePermissoes,
  useAmbienteControllerCriarCargo,
  useAmbienteControllerCriarConvite,
  useAmbienteControllerDefinirAtivoDoMembro,
  useAmbienteControllerDefinirCargoDoMembro,
  useAmbienteControllerDefinirPermissoes,
  useAmbienteControllerExcluirCargo,
  useAmbienteControllerListarCargos,
  useAmbienteControllerListarConvites,
  useAmbienteControllerListarMembros,
  useAmbienteControllerReenviarConvite,
  useAmbienteControllerRevogarConvite,
} from '@/api/generated/ambiente/ambiente';
import type {
  CargoDoAmbienteResponse,
  DefinirCargoDoMembroDtoUfCrmv,
  MembroDoAmbienteResponse,
} from '@/api/generated/model';
import { ApiError } from '@/api/http-client';
import { UFS } from '@/features/auth/schemas/login.schema';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { incluidaPor } from '@/shared/permissoes/incluidas';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import { Switch } from '@/shared/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { Textarea } from '@/shared/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shared/ui/tooltip';
import { toast } from '@/shared/ui/sonner';

/**
 * O que cada módulo governa, em tópicos — conteúdo do tooltip ao lado do nome
 * do módulo na tela de montagem de cargo. Chave = slug do módulo (`mod.modulo`),
 * o mesmo que o backend devolve em `catalogo-permissoes`.
 *
 * Fica no front (e não no catálogo do servidor) de propósito: é copy de ajuda
 * desta tela, não regra de autorização.
 */
const DESCRICAO_DO_MODULO: Record<string, string[]> = {
  ambiente: [
    'Convidar pessoas para o ambiente',
    'Criar e editar cargos',
    'Definir o que cada cargo acessa',
    'Dê só a quem vai administrar no seu lugar',
  ],
  analises: [
    'Criar e editar grupos de pacientes',
    'Excluir grupos salvos',
    'Abrir os gráficos comparativos',
  ],
  analises_ia: ['Gerar análise por IA (consome créditos)', 'Ler o texto já gerado'],
  discussoes: [
    'Consultar as discussões com especialistas',
    'Abrir discussão nova continua sendo só do médico',
  ],
  diagnosticos: [
    'Registrar diagnósticos na ficha clínica',
    'Editar e remover diagnósticos',
  ],
  exames: [
    'Lançar exames manualmente',
    'Editar e remover lançamentos',
    'Consultar os laudos recebidos',
  ],
  historico: ['Consultar o histórico clínico consolidado (só leitura)'],
  materiais: ['Ver os materiais que o parceiro publica no portal dele'],
  pacientes: [
    'Cadastrar pacientes e tutores',
    'Editar e arquivar cadastros',
    'Consultar a lista',
  ],
  parceiros: ['Consultar a vitrine de parceiros e os materiais deles'],
  prontuarios: [
    'Editar e consultar os atendimentos',
    'Criar prontuário novo é ato do médico (não entra aqui)',
  ],
  receituarios: [
    'Redigir (editar), remover e consultar receitas',
    '“Assinar”: assina no lugar do médico — conceda com cautela',
    'Criar a receita é do médico',
  ],
  requisicoes_exames: [
    'Editar, remover e consultar solicitações',
    '“Assinar”: assina a requisição',
    'A criação é do médico',
  ],
  resultados: ['Editar os valores lançados', 'Consultar os resultados'],
  tratamentos: [
    'Criar e editar tratamentos e ajustes de dose',
    'Suspender tratamentos',
    'Consultar o histórico',
  ],
  tutores: ['Cadastrar e editar os tutores (donos dos pacientes)'],
  unidades: ['Consultar as unidades e locais de atendimento'],
};

/**
 * "Minha equipe" — administração do PRÓPRIO ambiente pelo médico dono: convidar
 * pessoas, montar cargos com as permissões que ele quiser, mudar cargo /
 * desativar membro. Tenant-scoped no servidor (`ambiente.*`, escopo CLINICA).
 */
export function EquipePage() {
  const { pode } = usePermissoes();
  const qc = useQueryClient();

  const membros = useAmbienteControllerListarMembros();
  const convites = useAmbienteControllerListarConvites();
  const cargos = useAmbienteControllerListarCargos();

  const definirCargo = useAmbienteControllerDefinirCargoDoMembro();
  const definirAtivo = useAmbienteControllerDefinirAtivoDoMembro();
  const reenviar = useAmbienteControllerReenviarConvite();
  const revogar = useAmbienteControllerRevogarConvite();
  const excluirCargoMutation = useAmbienteControllerExcluirCargo();

  const podeCriar = pode('ambiente.criar');
  const podeEditar = pode('ambiente.editar');
  const podeExcluir = pode('ambiente.excluir');

  const [convidando, setConvidando] = useState(false);
  const [cargoEmForm, setCargoEmForm] = useState<CargoDoAmbienteResponse | 'novo' | null>(null);
  const [cargoAExcluir, setCargoAExcluir] = useState<CargoDoAmbienteResponse | null>(null);
  const [promovendo, setPromovendo] = useState<{
    membro: MembroDoAmbienteResponse;
    cargoId: string;
  } | null>(null);

  /** O único cargo de sistema na lista (`tenantId` nulo) é o "Médico". */
  const cargoMedicoId = useMemo(
    () => (cargos.data ?? []).find((c) => c.tenantId === null)?.id ?? null,
    [cargos.data],
  );

  const invalidarTudo = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: getAmbienteControllerListarMembrosQueryKey() }),
      qc.invalidateQueries({ queryKey: getAmbienteControllerListarConvitesQueryKey() }),
      qc.invalidateQueries({ queryKey: getAmbienteControllerListarCargosQueryKey() }),
    ]);

  async function trocarCargo(membro: MembroDoAmbienteResponse, cargoId: string) {
    if (cargoId === membro.cargoId) return;
    // Virar Médico sem CRMV no perfil → pede CRMV + UF num diálogo antes de gravar.
    if (cargoId === cargoMedicoId && !membro.temCrmv) {
      setPromovendo({ membro, cargoId });
      return;
    }
    try {
      await definirCargo.mutateAsync({ profileId: membro.id, data: { cargoId } });
      await invalidarTudo();
      toast.success('Cargo atualizado.');
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Não foi possível trocar o cargo.');
    }
  }

  async function trocarAtivo(profileId: string, ativo: boolean) {
    try {
      await definirAtivo.mutateAsync({ profileId, data: { ativo } });
      await invalidarTudo();
      toast.success(ativo ? 'Membro reativado.' : 'Membro desativado.');
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Não foi possível mudar o status.');
    }
  }

  async function reenviarConvite(id: string) {
    try {
      await reenviar.mutateAsync({ id });
      await qc.invalidateQueries({ queryKey: getAmbienteControllerListarConvitesQueryKey() });
      toast.success('Convite reenviado.');
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Não foi possível reenviar.');
    }
  }

  async function revogarConvite(id: string) {
    try {
      await revogar.mutateAsync({ id });
      await qc.invalidateQueries({ queryKey: getAmbienteControllerListarConvitesQueryKey() });
      toast.success('Convite revogado.');
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Não foi possível revogar.');
    }
  }

  async function excluirCargoConfirmado(cargo: CargoDoAmbienteResponse) {
    try {
      await excluirCargoMutation.mutateAsync({ id: cargo.id });
      await qc.invalidateQueries({ queryKey: getAmbienteControllerListarCargosQueryKey() });
      toast.success(`Cargo "${cargo.nome}" excluído.`);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Não foi possível excluir.');
    }
  }

  return (
    <div className="space-y-8">
      {/* ── Membros ─────────────────────────────────────────────────────── */}
      <Painel titulo="Membros" icone={Users}>
        {membros.isPending ? (
          <SkeletonLinhas />
        ) : membros.isError || !membros.data ? (
          <EstadoErro aoTentarDeNovo={() => void membros.refetch()} />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Pessoa</TableHead>
                <TableHead>Cargo</TableHead>
                <TableHead className="w-24">Ativo</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {membros.data.map((m) => (
                <TableRow key={m.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-foreground">{m.nomeCompleto}</span>
                      {m.ehDono && (
                        <Badge variant="outline" className="text-xs">
                          dono
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">{m.email}</p>
                  </TableCell>
                  <TableCell>
                    {m.ehDono || !podeEditar ? (
                      <span className="text-sm text-muted-foreground">{m.cargoNome ?? '—'}</span>
                    ) : (
                      <Select
                        value={m.cargoId ?? undefined}
                        onValueChange={(v) => void trocarCargo(m, v)}
                      >
                        <SelectTrigger className="w-52">
                          <SelectValue placeholder="Sem cargo" />
                        </SelectTrigger>
                        <SelectContent>
                          {(cargos.data ?? []).map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                              {c.nome}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={m.ativo}
                      disabled={m.ehDono || !podeEditar}
                      onCheckedChange={(v) => void trocarAtivo(m.id, v)}
                      aria-label={`Ativo: ${m.nomeCompleto}`}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Painel>

      {/* ── Convites ────────────────────────────────────────────────────── */}
      <Painel
        titulo="Convites pendentes"
        icone={Mail}
        acao={
          podeCriar ? (
            <Button size="sm" onClick={() => setConvidando(true)}>
              <Plus className="w-4 h-4" />
              Convidar
            </Button>
          ) : undefined
        }
      >
        {convites.isPending ? (
          <SkeletonLinhas />
        ) : convites.isError || !convites.data ? (
          <EstadoErro aoTentarDeNovo={() => void convites.refetch()} />
        ) : convites.data.length === 0 ? (
          <EstadoVazio icone={Mail} titulo="Nenhum convite pendente" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>E-mail</TableHead>
                <TableHead>Cargo</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-32">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {convites.data.map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="font-medium text-foreground">{c.email}</TableCell>
                  <TableCell className="text-muted-foreground">{c.cargoNome ?? '—'}</TableCell>
                  <TableCell>
                    {c.expirado ? (
                      <Badge variant="outline" className="text-destructive border-destructive/40">
                        expirado
                      </Badge>
                    ) : (
                      <Badge variant="outline">pendente</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {podeCriar && (
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Reenviar"
                          onClick={() => void reenviarConvite(c.id)}
                        >
                          <RotateCw className="w-4 h-4" />
                        </Button>
                      )}
                      {podeExcluir && (
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Revogar"
                          className="text-destructive"
                          onClick={() => void revogarConvite(c.id)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Painel>

      {/* ── Cargos do ambiente ──────────────────────────────────────────── */}
      <Painel
        titulo="Cargos do ambiente"
        icone={Shield}
        acao={
          podeCriar ? (
            <Button size="sm" variant="outline" onClick={() => setCargoEmForm('novo')}>
              <Plus className="w-4 h-4" />
              Novo cargo
            </Button>
          ) : undefined
        }
      >
        {cargos.isPending ? (
          <SkeletonLinhas />
        ) : cargos.isError || !cargos.data ? (
          <EstadoErro aoTentarDeNovo={() => void cargos.refetch()} />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cargo</TableHead>
                <TableHead>Permissões</TableHead>
                <TableHead>Membros</TableHead>
                <TableHead className="w-32">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {cargos.data.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-foreground">{c.nome}</span>
                      {c.sistema && (
                        <Badge variant="outline" className="text-xs">
                          sistema
                        </Badge>
                      )}
                    </div>
                    {c.descricao && (
                      <p className="text-xs text-muted-foreground mt-0.5">{c.descricao}</p>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{c.permissoes.length}</TableCell>
                  <TableCell className="text-muted-foreground">{c.totalMembros}</TableCell>
                  <TableCell>
                    {!c.sistema && (
                      <div className="flex gap-1">
                        {podeEditar && (
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Permissões de ${c.nome}`}
                            onClick={() => setCargoEmForm(c)}
                          >
                            <KeyRound className="w-4 h-4" />
                          </Button>
                        )}
                        {podeExcluir && c.totalMembros === 0 && (
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Excluir ${c.nome}`}
                            className="text-destructive"
                            onClick={() => setCargoAExcluir(c)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Painel>

      {convidando && (
        <DialogoDeConvite
          cargos={cargos.data ?? []}
          aoFechar={() => setConvidando(false)}
          aoConvidar={() => {
            void qc.invalidateQueries({
              queryKey: getAmbienteControllerListarConvitesQueryKey(),
            });
          }}
        />
      )}

      {cargoEmForm && (
        <DialogoDeCargo
          cargo={cargoEmForm === 'novo' ? null : cargoEmForm}
          aoFechar={() => setCargoEmForm(null)}
          aoSalvar={() => {
            void qc.invalidateQueries({
              queryKey: getAmbienteControllerListarCargosQueryKey(),
            });
          }}
        />
      )}

      {promovendo && (
        <DialogoCrmvParaMedico
          membro={promovendo.membro}
          aoFechar={() => setPromovendo(null)}
          aoConfirmar={async (crmv, ufCrmv) => {
            await definirCargo.mutateAsync({
              profileId: promovendo.membro.id,
              data: { cargoId: promovendo.cargoId, crmv, ufCrmv },
            });
            await invalidarTudo();
            toast.success('Cargo atualizado.');
            setPromovendo(null);
          }}
        />
      )}

      <AlertDialog
        open={Boolean(cargoAExcluir)}
        onOpenChange={(v) => !v && setCargoAExcluir(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir o cargo "{cargoAExcluir?.nome}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Não está atribuído a ninguém. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const alvo = cargoAExcluir;
                setCargoAExcluir(null);
                if (alvo) void excluirCargoConfirmado(alvo);
              }}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Painel({
  titulo,
  icone: Icone,
  acao,
  children,
}: {
  titulo: string;
  icone: typeof Users;
  acao?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="bg-surface-raised rounded-lg border-2 border-brand/20 overflow-hidden">
      <div className="px-6 py-4 bg-gradient-to-r from-brand/5 to-transparent border-b border-brand/10 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-brand uppercase tracking-wide flex items-center gap-2">
          <Icone className="w-4 h-4" />
          {titulo}
        </h3>
        {acao}
      </div>
      <div className="p-2">{children}</div>
    </section>
  );
}

function SkeletonLinhas() {
  return (
    <div className="p-4 space-y-3">
      {[0, 1, 2].map((i) => (
        <Skeleton key={i} className="h-10 rounded-md" />
      ))}
    </div>
  );
}

/**
 * Pede CRMV + UF quando o médico promove a "Médico" um membro que ainda não tem
 * registro no perfil. O backend recusa a troca sem isso (422 CRMV_OBRIGATORIO).
 */
function DialogoCrmvParaMedico({
  membro,
  aoFechar,
  aoConfirmar,
}: {
  membro: MembroDoAmbienteResponse;
  aoFechar: () => void;
  aoConfirmar: (crmv: string, ufCrmv: DefinirCargoDoMembroDtoUfCrmv) => Promise<void>;
}) {
  const [crmv, setCrmv] = useState('');
  const [uf, setUf] = useState<DefinirCargoDoMembroDtoUfCrmv | ''>('');
  const [salvando, setSalvando] = useState(false);

  async function confirmar() {
    if (!crmv.trim() || !uf) return;
    setSalvando(true);
    try {
      await aoConfirmar(crmv.trim(), uf);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Não foi possível concluir.');
      setSalvando(false);
    }
  }

  return (
    <Dialog open onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Informe o CRMV de {membro.nomeCompleto}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            O cargo de Médico exige registro profissional. O CRMV fica salvo no perfil deste membro.
          </p>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2 space-y-2">
              <Label htmlFor="promo-crmv">CRMV</Label>
              <Input
                id="promo-crmv"
                placeholder="Número do CRMV"
                value={crmv}
                onChange={(e) => setCrmv(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="promo-uf">UF</Label>
              <Select
                value={uf || undefined}
                onValueChange={(v) => setUf(v as DefinirCargoDoMembroDtoUfCrmv)}
              >
                <SelectTrigger id="promo-uf">
                  <SelectValue placeholder="UF" />
                </SelectTrigger>
                <SelectContent>
                  {UFS.map((u) => (
                    <SelectItem key={u} value={u}>
                      {u}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button disabled={!crmv.trim() || !uf || salvando} onClick={() => void confirmar()}>
            {salvando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Salvar e promover
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DialogoDeConvite({
  cargos,
  aoFechar,
  aoConvidar,
}: {
  cargos: CargoDoAmbienteResponse[];
  aoFechar: () => void;
  aoConvidar: () => void;
}) {
  const [email, setEmail] = useState('');
  const [cargoId, setCargoId] = useState('');
  const criar = useAmbienteControllerCriarConvite();
  const atribuiveis = cargos.filter((c) => c.tenantId !== null || c.nome === 'Médico');

  async function enviar() {
    try {
      await criar.mutateAsync({ data: { email: email.trim(), cargoId } });
      aoConvidar();
      toast.success('Convite enviado.');
      aoFechar();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Não foi possível convidar.');
    }
  }

  return (
    <Dialog open onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCog className="w-5 h-5 text-brand" />
            Convidar para o ambiente
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="convite-email">E-mail</Label>
            <Input
              id="convite-email"
              type="email"
              placeholder="pessoa@exemplo.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Só funciona para quem ainda não tem conta na plataforma.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="convite-cargo">Cargo</Label>
            <Select value={cargoId} onValueChange={setCargoId}>
              <SelectTrigger id="convite-cargo">
                <SelectValue placeholder="Escolha um cargo" />
              </SelectTrigger>
              <SelectContent>
                {atribuiveis.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {atribuiveis.find((c) => c.id === cargoId)?.tenantId === null && (
              <p className="text-xs text-muted-foreground">
                Cargo de Médico: a pessoa terá que informar o CRMV e a UF ao aceitar o convite.
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button
            disabled={!email.trim() || !cargoId || criar.isPending}
            onClick={() => void enviar()}
          >
            {criar.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Enviar convite
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DialogoDeCargo({
  cargo,
  aoFechar,
  aoSalvar,
}: {
  cargo: CargoDoAmbienteResponse | null;
  aoFechar: () => void;
  aoSalvar: () => void;
}) {
  const catalogo = useAmbienteControllerCatalogoDePermissoes();
  const criar = useAmbienteControllerCriarCargo();
  const definirPermissoes = useAmbienteControllerDefinirPermissoes();

  const [nome, setNome] = useState(cargo?.nome ?? '');
  const [descricao, setDescricao] = useState(cargo?.descricao ?? '');
  const [marcadas, setMarcadas] = useState<Set<string>>(new Set(cargo?.permissoes ?? []));

  const alternar = (chave: string) =>
    setMarcadas((prev) => {
      const n = new Set(prev);
      if (n.has(chave)) n.delete(chave);
      else n.add(chave);
      return n;
    });

  const editando = cargo !== null;

  const modulos = useMemo(() => catalogo.data?.modulos ?? [], [catalogo.data]);
  const chavesDoCatalogo = useMemo(
    () => new Set(modulos.flatMap((m) => m.permissoes.map((p) => p.chave))),
    [modulos],
  );

  async function salvar() {
    try {
      // Só manda o que ainda está no catálogo: um cargo antigo pode carregar
      // uma chave que deixou de ser concedível (ex.: `historico.visualizar`),
      // e mandá-la de volta levaria a um 409 sem checkbox para desmarcar.
      const permissoes = [...marcadas].filter(
        (c) => chavesDoCatalogo.size === 0 || chavesDoCatalogo.has(c),
      );
      if (editando) {
        await definirPermissoes.mutateAsync({ id: cargo.id, data: { permissoes } });
      } else {
        await criar.mutateAsync({
          data: { nome: nome.trim(), descricao: descricao.trim() || null, permissoes },
        });
      }
      aoSalvar();
      toast.success(editando ? 'Permissões atualizadas.' : 'Cargo criado.');
      aoFechar();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Não foi possível salvar.');
    }
  }

  const ocupado = criar.isPending || definirPermissoes.isPending;

  return (
    <Dialog open onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editando ? `Permissões — ${cargo.nome}` : 'Novo cargo'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
          {!editando && (
            <>
              <div className="space-y-2">
                <Label htmlFor="cargo-nome">Nome</Label>
                <Input
                  id="cargo-nome"
                  placeholder="Ex.: Recepção"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cargo-desc">Descrição (opcional)</Label>
                <Textarea
                  id="cargo-desc"
                  rows={2}
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                />
              </div>
            </>
          )}

          <div className="space-y-3">
            <Label>Permissões</Label>
            {catalogo.isPending ? (
              <SkeletonLinhas />
            ) : (
              <TooltipProvider delayDuration={150}>
                {modulos.map((mod) => {
                  const topicos = DESCRICAO_DO_MODULO[mod.modulo] ?? [];
                  return (
                    <div key={mod.modulo} className="rounded-md border border-border p-3">
                      <div className="mb-2 flex items-center gap-1.5">
                        <p className="text-sm font-medium text-foreground">{mod.moduloLabel}</p>
                        {topicos.length > 0 && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                aria-label={`O que é ${mod.moduloLabel}`}
                                className="rounded text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
                              >
                                <Info className="h-3.5 w-3.5" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <ul className="list-disc space-y-1 pl-4">
                                {topicos.map((item) => (
                                  <li key={item}>{item}</li>
                                ))}
                              </ul>
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {mod.permissoes.map((p) => {
                          // Incluída por outra marcada (ex.: Tutores ← Pacientes):
                          // aparece marcada e travada, porque o servidor já a
                          // concede — desmarcar aqui não tiraria nada.
                          const origem = marcadas.has(p.chave) ? null : incluidaPor(p.chave, marcadas);
                          return (
                            <label key={p.chave} className="flex items-center gap-2 text-sm">
                              <Checkbox
                                checked={marcadas.has(p.chave) || origem !== null}
                                disabled={origem !== null}
                                onCheckedChange={() => alternar(p.chave)}
                              />
                              {p.acaoLabel}
                            </label>
                          );
                        })}
                      </div>
                      {mod.permissoes.some(
                        (p) => !marcadas.has(p.chave) && incluidaPor(p.chave, marcadas),
                      ) && (
                        <p className="mt-2 text-xs text-muted-foreground">
                          Incluídas pelas permissões de Pacientes: quem cadastra e edita
                          paciente também cadastra e edita o responsável.
                        </p>
                      )}
                    </div>
                  );
                })}
              </TooltipProvider>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button
            disabled={ocupado || (!editando && !nome.trim())}
            onClick={() => void salvar()}
          >
            {ocupado && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
